import React, {useEffect, useState} from 'react';
import {Controller} from "react-hook-form";
import debounce from 'lodash/debounce'
import {trackingDomainServices} from "../../service/trackingDomainService";
import {Autocomplete, Box, TextField} from "@mui/material";
import {Info} from "@mui/icons-material";
import {CustomTooltip} from "../landings/LandingForm";

export default function TrackingDomainSelect({control, errors, setValue, watch, compact = false}) {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [requestError, setRequestError] = useState('');
    const [defaultValue, setDefaultValue] = useState(null);

    useEffect(() => {
        async function fetchData(): void {
            try {
                setLoading(true);
                setRequestError('');
                const response = await trackingDomainServices.read(search);
                setOptions(
                    response.trackingDomains.map(item => {
                        return {
                            value: item.id,
                            label: item.domain
                        }
                    })
                );
            } catch (e) {
                setRequestError('Unable to load tracking domains. Close and reopen the list to retry.');
            } finally {
                setLoading(false);
            }
        }

        if (open && (search.length === 0 || search.length > 0)) {
            fetchData();
        }

    }, [search, open]);

    useEffect(() => {
        if (!open) {
            setOptions([]);
            setSearch("");
        }
    }, [open]);

    useEffect(() => {
        if (defaultValue === null && (watch("trackingDomain") === null || watch("trackingDomain") === undefined)) {
            let userSettings;
            try {userSettings = JSON.parse(localStorage.getItem('user-settings'));} catch {userSettings = null;}
            if (userSettings?.defaultTrackingDomain) {
                setLoading(true);
                trackingDomainServices.read(userSettings?.defaultTrackingDomain).then((response) => {
                    response?.trackingDomains?.forEach(item => {
                        if (item?.domain === userSettings?.defaultTrackingDomain) {
                            setDefaultValue({
                                value: item?.id,
                                label: item?.domain
                            });
                            setValue("trackingDomain", {
                                value: item?.id,
                                label: item?.domain
                            });
                        }
                    })
                }).catch(() => {
                    setRequestError('Unable to load your default tracking domain. Select a domain from the list.');
                }).finally(() => {
                    setLoading(false);
                });
            }
        }
    }, [watch, setValue, defaultValue]);

    const handleSearch = debounce(searchTerm => {
        setSearch(searchTerm);
    }, 800);

    return (
        <Controller
            render={({field: {onChange, ...props}, fieldState}) => (
                <Autocomplete
                    sx={{paddingTop: compact ? 0 : "4px"}}
                    id="tracking-domain-select"
                    open={open}
                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => {
                        setOpen(false);
                    }}
                    isOptionEqualToValue={(option, value) => option.label === value.label}
                    getOptionLabel={(option) => option.label}
                    options={options}
                    loading={loading}
                    renderInput={params => (
                        <Box display="flex" sx={{paddingTop: compact ? 0 : "10px", minWidth: 0}}>
                            <Box flexGrow={1} sx={{minWidth: 0}}>
                                <TextField
                                    {...params}
                                    error={!!fieldState.error || !!requestError}
                                    placeholder={compact ? 'Select a domain' : 'Select a tracking domain'}
                                    variant="outlined"
                                    margin={compact ? 'none' : 'dense'}
                                    size="small"
                                    label="Tracking domain*"
                                    helperText={fieldState.error?.message || requestError || undefined}
                                    value={search}
                                    onChange={e => handleSearch(e.target.value)}
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />
                            </Box>
                            {!compact && <Box p={1}>
                                <Box sx={{paddingTop: "10px"}}>
                                    <CustomTooltip
                                        disableFocusListener
                                        title="Choose domain to run your campaign. We highly recommend to add your custom domains.
                                             Our provided default domains are meant for testing purposes only and is not recommended to use in production.
                                             Add custom domains in Settings view."
                                    >
                                        <Info color={"primary"} fontSize={"small"}/>
                                    </CustomTooltip>
                                </Box>
                            </Box>}
                        </Box>
                    )}
                    onChange={(e, data) => {setRequestError(''); onChange(data);}}
                    {...props}
                />
            )}
            rules={{required: 'Please select a tracking domain.', validate: value => value?.value > 0 || 'Please select a tracking domain.'}}
            name="trackingDomain"
            control={control}
            defaultValue={defaultValue}
            onChange={([, data]) => data}
        />
    );
}
